import { create } from 'zustand';
import { useGlobalStore } from './globalStore';

/**
 * How many EHR messages need someone to act (no match, values to map, or held
 * behind one of those): the Pending Messages "Action Needed" count.
 *
 * Shown as a badge on the EHR item in the sidebar and on the Pending Messages
 * tab. Two writers keep it current without any extra requests: the alert poll
 * (useAlertPolling), which already fetches the pending list once a minute for
 * anyone who can see EHR, and the Pending Messages page after each of its own
 * loads, so the badge drops as soon as someone clears a message there.
 *
 * Kept per facility so a facility switch never shows the last facility's count.
 */
interface EhrActionState {
  facilityId: number;
  count: number;
  setActionNeeded: (facilityId: number, count: number) => void;
}

export const useEhrActionStore = create<EhrActionState>((set) => ({
  facilityId: 0,
  count: 0,
  setActionNeeded: (facilityId, count) => set({ facilityId, count }),
}));

/**
 * The Action Needed count for the current facility, or 0 for someone who can't
 * open the EHR pages (Dietech_Ehr_Level 0) or where the facility's EHR is off.
 */
export function useEhrActionNeededCount(): number {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const canSeeEhr = useGlobalStore((s) =>
    (s.userRole?.dietech_Ehr_Level ?? 0) > 0
    && !!(s.facilitySettings as Record<string, unknown> | null)?.facilityEhrActive);
  const count = useEhrActionStore((s) => (s.facilityId === facilityId ? s.count : 0));
  return canSeeEhr ? count : 0;
}

/** "6 need action" / "1 needs action": the badge's spoken label. */
export function ehrActionLabel(count: number): string {
  return `${count} ${count === 1 ? 'needs' : 'need'} action`;
}
